import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import AuthLayout from '../../components/layout/AuthLayout';
import IconAdd from '../../components/images/IconAdd.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { useDeletePAChatMutation, useGetPAChatsQuery, useUpdatePATitleMutation } from '../../api/speech.js';
import IconDots from '../../components/images/IconDots.jsx';
import IconSuccess from '../../components/images/IconSuccess.jsx';
import IconCloseFull from '../../components/images/IconCloseFull.jsx';
import Speechify from './Speechify.jsx';
import IconSettings from '../../components/images/IconSettings.jsx';

const PersonalChats = () => {
    const [chats, setChats] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [editingChatId, setEditingChatId] = useState(null);
    const [editedTitle, setEditedTitle] = useState('');
    const [visible, setVisible] = useState(false);

    const {
        data: apiResult,
        isFetching,
        isLoading
    } = useGetPAChatsQuery({ page, per_page: 15 }, { refetchOnMountOrArgChange: true });

    const pageData = apiResult?.response;

    const [deletePAChat] = useDeletePAChatMutation();
    const [updatePATitle] = useUpdatePATitleMutation();

    const menuContainerRef = useRef(null);
    useEffect(() => {
        if (!openMenuId) return;
        const handleClickOutside = (e) => {
            if (!e.target.closest('.menu-container')) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuId]);

    useEffect(() => {
        if (!pageData?.chats) return;

        if (page === 1) {
            setChats(pageData.chats);
        } else {
            setChats((prev) => {
                const newItems = pageData.chats.filter((nc) => !prev.some((pc) => pc.id === nc.id));
                return [...prev, ...newItems];
            });
        }

        setHasMorePages(pageData.has_more_pages);
    }, [pageData, page]);

    useEffect(() => {
        let processing = false;
        const onScroll = () => {
            if (
                Math.round(window.innerHeight + document.documentElement.scrollTop) + 10 >=
                    document.documentElement.offsetHeight &&
                hasMorePages &&
                !isFetching &&
                !processing
            ) {
                processing = true;
                setPage((prev) => prev + 1);
                setTimeout(() => (processing = false), 500);
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [hasMorePages, isFetching]);

    const handleDelete = async (sessionId) => {
        await deletePAChat({ sessionId });
        setChats((prev) => prev.filter((c) => c.session_id !== sessionId));
        setOpenMenuId(null);
    };

    const handleEditInit = (chat) => {
        setEditingChatId(chat.id);
        setEditedTitle(chat.title);
        setOpenMenuId(null);
    };

    const handleEditDone = async (chat) => {
        await updatePATitle({ sessionId: chat.session_id, data: { title: editedTitle } });
        setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, title: editedTitle } : c)));
        setEditingChatId(null);
    };

    return (
        <AuthLayout
            title='Personal Chats'
            backRoute='/dashboard'
            forwardRoute={`/personal-assistant`}
            forwardIcon={IconAdd}
            forwardIconClassName='fill-theme1-white'
        >
            <div className='py-2 px-8 max-w-3xl w-full mx-auto mb-6'>
                <div className='flex justify-end'>
                    <div className='flex gap-1'>
                        <span className='font-light text-sm cursor-pointer' onClick={() => setVisible(true)}>
                            Voice Settings
                        </span>
                        <IconSettings className='w-5 h-5' />
                    </div>
                </div>
                {!isLoading && !isFetching && chats.length === 0 && (
                    <div className='py-4 text-center'>You have no chats yet.</div>
                )}
                <ul>
                    {chats.map((chat) => (
                        <li
                            key={chat.id}
                            className='flex items-center justify-between py-4 border-b border-solid border-theme1-tanTransparent'
                        >
                            <div className='flex-1'>
                                {editingChatId === chat.id ? (
                                    <div className='flex items-center gap-2'>
                                        <input
                                            className='flex-grow p-2 rounded border'
                                            value={editedTitle}
                                            onChange={(e) => setEditedTitle(e.target.value)}
                                        />
                                        <button className='border-none bg-none' onClick={() => handleEditDone(chat)}>
                                            <IconSuccess className='fill-theme1-primary w-[29px]' />
                                        </button>
                                        <button className='border-none bg-none' onClick={() => setEditingChatId(null)}>
                                            <IconCloseFull className='fill-theme1-primary' />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <Link
                                            to={`/personal-assistant/${chat.session_id}`}
                                            className='text-base font-bold text-theme1-title2'
                                        >
                                            {chat.title}
                                        </Link>
                                        <div className='text-xs text-theme1-text2 mt-1'>
                                            {dayjs(chat.created_at).format('MMM D, YYYY')}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className='relative menu-container' ref={menuContainerRef}>
                                <button
                                    onClick={() => setOpenMenuId(openMenuId === chat.id ? null : chat.id)}
                                    className='p-2 rounded hover:bg-theme1-hover'
                                >
                                    <IconDots className='fill-theme1-text1' />
                                </button>

                                {openMenuId === chat.id && (
                                    <div className='absolute right-0 mt-2 bg-white text-black rounded shadow-lg z-10'>
                                        <button
                                            className='block w-full text-left px-4 py-2 hover:bg-gray-100'
                                            onClick={() => handleDelete(chat.session_id)}
                                        >
                                            Delete
                                        </button>
                                        <button
                                            className='block w-full text-left px-4 py-2 hover:bg-gray-100'
                                            onClick={() => handleEditInit(chat)}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
                {(isLoading || isFetching) && (
                    <div className='flex justify-center py-4'>
                        <ThreeDotsLoader />
                    </div>
                )}

                <Speechify visible={visible} setVisible={setVisible} />
            </div>
        </AuthLayout>
    );
};

export default PersonalChats;
